import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/hooks/useAuth";
import { useWatchlist } from "@/hooks/useWatchlist";
import SummaryBar from "@/components/SummaryBar";
import FilterRow from "@/components/FilterRow";
import StockTable from "@/components/StockTable";
import StockDetail from "@/components/StockDetail";
import MarketBar from "@/components/MarketBar";
import NewsFeed from "@/components/NewsFeed";
import ArticleReader from "@/components/ArticleReader";
import AlertBanner from "@/components/AlertBanner";
import PortfolioScreener from "@/components/PortfolioScreener";
import PortfolioTracker from "@/components/PortfolioTracker";
import EconomicCalendarView from "@/components/EconomicCalendarView";
import NotificationCenter from "@/components/NotificationCenter";
import UpgradeModal from "@/components/UpgradeModal";
import SectorAnalytics from "@/components/SectorAnalytics";
import DashboardStatsCards from "@/components/DashboardStatsCards";
import DashboardBottomCards, { pushRecentlyViewed, pushScreeningHistory } from "@/components/DashboardBottomCards";
import { DownloadSimple, Star, ListBullets, Warning, Briefcase, ShieldCheck, SignOut, UserCircle, ChartLine, ListChecks, House, CalendarBlank, ChartPieSlice } from "@phosphor-icons/react";
import { analytics } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ onNavigate, initialTab, initialStock, prefetchedStocks }) {
  const { dark } = useTheme();
  const { user, showAuthModal, logout } = useAuth();
  const watchlist = useWatchlist();
  const [stocks, setStocks] = useState(prefetchedStocks || []);
  const [summary, setSummary] = useState({ total: 0, compliant: 0, borderline: 0, non_compliant: 0, pass_rate: 0 });
  const [sectors, setSectors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(!prefetchedStocks);
  const [selectedStock, setSelectedStock] = useState(null);
  const [articleUrl, setArticleUrl] = useState(null);
  const [viewMode, setViewMode] = useState(initialTab || "all");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [fmpSuggestions, setFmpSuggestions] = useState([]);
  const [portfolioSubTab, setPortfolioSubTab] = useState("tracker");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Auto-open a stock if passed from landing page search
  useEffect(() => {
    if (initialStock) {
      const ticker = initialStock.toUpperCase().trim();
      axios.get(`${API}/stocks/lookup/${ticker}`).then(res => {
        if (res.data) setSelectedStock(res.data);
      }).catch(() => {});
    }
  }, [initialStock]);

  const fetchStocks = useCallback(async () => {
    if (!stocks.length) setLoading(true);
    try {
      if (viewMode === "watchlist") {
        const res = await axios.get(`${API}/watchlist/${watchlist.sessionId}/stocks`);
        let data = res.data;
        if (statusFilter !== "all") data = data.filter((s) => s.verdict === statusFilter);
        if (sectorFilter !== "all") data = data.filter((s) => s.sector === sectorFilter);
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          data = data.filter((s) => s.ticker.toLowerCase().includes(q) || s.company_name.toLowerCase().includes(q));
        }
        setStocks(data);
      } else {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (sectorFilter !== "all") params.sector = sectorFilter;
        if (search.trim()) params.search = search.trim();
        const res = await axios.get(`${API}/stocks`, { params });
        setStocks(res.data);
      }
    } catch (e) {
      console.error("Failed to fetch stocks:", e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sectorFilter, search, viewMode, watchlist.sessionId]);

  useEffect(() => {
    // Parallel fetch on mount
    Promise.all([
      axios.get(`${API}/stocks/summary`).then(r => setSummary(r.data)).catch(() => {}),
      axios.get(`${API}/sectors`).then(r => setSectors(r.data)).catch(() => {}),
    ]);
  }, []);

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  // Track recently viewed when a stock detail is opened
  useEffect(() => {
    if (selectedStock?.ticker) pushRecentlyViewed(selectedStock);
  }, [selectedStock]);

  // Track screening history (debounced) whenever filters yield a result set
  useEffect(() => {
    if (loading) return;
    if (viewMode !== "all" && viewMode !== "watchlist") return;
    if (statusFilter === "all" && sectorFilter === "all" && !search.trim()) return;
    const t = setTimeout(() => {
      const filters = [
        statusFilter !== "all" ? `status:${statusFilter}` : null,
        sectorFilter !== "all" ? `sector:${sectorFilter}` : null,
      ].filter(Boolean).join(" · ") || "no filters";
      pushScreeningHistory({
        label: search.trim() || (statusFilter !== "all" ? statusFilter.replace("_", " ") : sectorFilter !== "all" ? sectorFilter : "All Stocks"),
        query: search.trim() || null,
        filters,
        count: stocks.length,
      });
    }, 800);
    return () => clearTimeout(t);
  }, [statusFilter, sectorFilter, search, loading]);

  // Auto-refresh summary every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/stocks/summary`).then(r => setSummary(r.data)).catch(() => {});
      axios.get(`${API}/sectors`).then(r => setSectors(r.data)).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // FMP search suggestions (debounced)
  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) {
      setFmpSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/stocks/search-fmp`, { params: { query: search.trim() } });
        // Filter out stocks already in the table
        const existing = new Set(stocks.map((s) => s.ticker));
        setFmpSuggestions((res.data || []).filter((s) => !existing.has(s.ticker)));
      } catch {
        setFmpSuggestions([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search, stocks]);

  const handleLookup = async (ticker) => {
    setLookupLoading(true);
    setLookupError("");
    setFmpSuggestions([]);
    try {
      const res = await axios.get(`${API}/stocks/lookup/${ticker.toUpperCase()}`);
      if (res.data) {
        setSearch("");
        setSelectedStock(res.data);
        // Refresh data in background (non-blocking)
        fetchStocks();
        axios.get(`${API}/stocks/summary`).then(r => setSummary(r.data)).catch(() => {});
        axios.get(`${API}/sectors`).then(r => setSectors(r.data)).catch(() => {});
      }
    } catch (e) {
      const msg = e.response?.data?.detail || `Could not find "${ticker}"`;
      setLookupError(msg);
      setTimeout(() => setLookupError(""), 4000);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && search.trim()) {
      const q = search.trim().toUpperCase();
      // If it looks like a ticker (1-5 uppercase letters), try lookup
      if (/^[A-Z]{1,5}$/.test(q) && !stocks.find((s) => s.ticker === q)) {
        handleLookup(q);
      }
    }
  };

  const handleExportCSV = () => {
    showAuthModal(() => {
      analytics.csvExport();
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (sectorFilter !== "all") params.set("sector", sectorFilter);
      window.open(`${API}/stocks/export/csv?${params.toString()}`, "_blank");
    });
  };

  const handlePremiumTab = (tab) => {
    setViewMode(tab);
  };

  return (
    <div className="w-full min-h-screen transition-colors" style={{ background: "#090B10", color: "#e4e4e7" }} data-testid="dashboard">
      <MarketBar />

      <div className="w-full max-w-none px-4 sm:px-5 md:px-10 py-6 sm:py-8">
        {/* Premium fintech header */}
        <header className="mb-6" data-testid="dashboard-header">
          <div className="flex items-center justify-between gap-4 mb-6">
            <button
              onClick={() => onNavigate("landing")}
              className="flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              data-testid="back-to-home"
            >
              <House size={16} weight="regular" />
              <span>Halal Screener</span>
              <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded" style={{ background: "rgba(59,130,246,0.10)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.20)" }}>
                Terminal
              </span>
            </button>
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  <div className="flex items-center gap-2 px-3 h-9 rounded-lg" style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="user-info">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <UserCircle size={14} weight="bold" className="text-zinc-400" />
                    )}
                    <span className="text-[11.5px] text-zinc-300 hidden sm:inline max-w-[120px] truncate">
                      {user.name || user.email}
                    </span>
                  </div>
                  <button onClick={logout} className="w-9 h-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white transition-colors" style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="logout-btn" title="Sign out">
                    <SignOut size={14} weight="bold" />
                  </button>
                </>
              ) : (
                <button onClick={() => showAuthModal()} className="px-3.5 h-9 rounded-lg text-[12px] font-semibold text-black transition-transform hover:scale-[1.02]" style={{ background: "#ffffff" }} data-testid="sign-in-btn">
                  Sign In
                </button>
              )}
            </div>
          </div>

          {/* Search + actions row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
                onKeyDown={(e) => {
                  handleSearchKeyDown(e);
                  if (e.key === "Escape") setShowSuggestions(false);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Search any company or ticker..."
                className="w-full h-10 pl-10 pr-16 rounded-lg text-[13px] outline-none transition-colors focus:border-white/20"
                style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.06)", color: "#e4e4e7" }}
                data-testid="dashboard-search-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: "rgba(255,255,255,0.42)", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                {lookupLoading ? "..." : "⌘K"}
              </span>

              {/* FMP look-up suggestions */}
              {showSuggestions && fmpSuggestions && fmpSuggestions.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-1.5 z-50 rounded-lg overflow-hidden max-h-[240px] overflow-y-auto shadow-2xl"
                  style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.10)" }}
                  data-testid="fmp-suggestions"
                >
                  <div className="px-3.5 py-1.5 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">Look up new ticker</span>
                  </div>
                  {fmpSuggestions.map((s) => (
                    <button
                      key={s.ticker}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setShowSuggestions(false); handleLookup(s.ticker); }}
                      className="w-full flex items-center justify-between px-3.5 py-2 text-left hover:bg-white/[0.04] transition-colors"
                      data-testid={`fmp-suggestion-${s.ticker}`}
                    >
                      <div className="min-w-0">
                        <span className="text-[13px] font-mono font-medium text-zinc-100">{s.ticker}</span>
                        <span className="text-[11px] text-zinc-500 ml-2 truncate">{s.company_name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-600 shrink-0 ml-3">{s.exchange}</span>
                    </button>
                  ))}
                </div>
              )}
              {lookupError && (
                <p className="absolute top-full left-1 mt-1 text-[11px] font-mono text-red-400 z-40" data-testid="lookup-error">{lookupError}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 h-10 px-3.5 rounded-lg text-[12px] font-medium text-zinc-300 hover:text-white transition-colors"
                style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid="export-csv-btn"
              >
                <DownloadSimple size={13} weight="bold" />
                <span className="hidden sm:inline">Export CSV</span>
              </button>
              <button
                onClick={fetchStocks}
                className="flex items-center gap-1.5 h-10 px-3.5 rounded-lg text-[12px] font-medium text-zinc-300 hover:text-white transition-colors"
                style={{ background: "#11151D", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid="refresh-btn"
                title="Refresh data"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></svg>
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <NotificationCenter onUpgrade={(msg) => { setUpgradeTrigger(msg); setUpgradeOpen(true); }} />
            </div>
          </div>
        </header>

        <AlertBanner />

        {/* Screener Results heading */}
        <div className="mt-2 mb-1" data-testid="screener-results-heading">
          <h1 className="text-[19px] sm:text-[21px] font-semibold text-white tracking-tight">Screener Results</h1>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            Showing results for {statusFilter !== "all" ? statusFilter.replace("_", "-") : "all"} stocks{sectorFilter !== "all" ? ` in ${sectorFilter}` : ""}
          </p>
        </div>

        <DashboardStatsCards summary={summary} />

        {/* Tabs */}
        <div className="flex items-center gap-0 mt-6 border-b overflow-x-auto" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="dashboard-tabs">
          <button
            onClick={() => setViewMode("all")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] border-b-2 transition-colors shrink-0 ${
              viewMode === "all" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
            data-testid="tab-all-stocks"
          >
            <ListBullets size={14} weight="bold" />
            All Stocks
          </button>
          <button
            onClick={() => setViewMode("watchlist")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] border-b-2 transition-colors shrink-0 ${
              viewMode === "watchlist" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
            data-testid="tab-watchlist"
          >
            <Star size={14} weight="bold" />
            Watchlist
            {watchlist.tickers.length > 0 && (
              <span className="text-[10px] font-mono px-1.5 py-px rounded" style={{ background: "rgba(255,255,255,0.06)", color: "#d4d4d8" }}>
                {watchlist.tickers.length}
              </span>
            )}
          </button>
          <button
            onClick={() => handlePremiumTab("portfolio")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] border-b-2 transition-colors shrink-0 ${
              viewMode === "portfolio" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
            data-testid="tab-portfolio"
          >
            <Briefcase size={14} weight="bold" />
            Portfolio
          </button>
          <button
            onClick={() => setViewMode("calendar")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] border-b-2 transition-colors shrink-0 ${
              viewMode === "calendar" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
            data-testid="tab-calendar"
          >
            <CalendarBlank size={14} weight="bold" />
            Calendar
          </button>
          <button
            onClick={() => setViewMode("sectors")}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] border-b-2 transition-colors shrink-0 ${
              viewMode === "sectors" ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-200"
            }`}
            data-testid="tab-sectors"
          >
            <ChartPieSlice size={14} weight="bold" />
            Sectors
          </button>
        </div>

        {viewMode === "portfolio" ? (
          <div>
            {/* Portfolio Sub-tabs */}
            <div className="flex items-center gap-0 mt-4 mb-2" data-testid="portfolio-sub-tabs">
              <button
                onClick={() => setPortfolioSubTab("tracker")}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                  portfolioSubTab === "tracker"
                    ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900"
                    : "text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 border border-zinc-200 dark:border-zinc-800"
                }`}
                data-testid="subtab-tracker"
              >
                <ChartLine size={12} weight="bold" />
                My Holdings
              </button>
              <button
                onClick={() => setPortfolioSubTab("screener")}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                  portfolioSubTab === "screener"
                    ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900"
                    : "text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 border border-zinc-200 dark:border-zinc-800"
                }`}
                data-testid="subtab-screener"
              >
                <ListChecks size={12} weight="bold" />
                Bulk Screen
              </button>
            </div>
            {portfolioSubTab === "tracker" ? (
              <PortfolioTracker onSelectStock={setSelectedStock} />
            ) : (
              <PortfolioScreener onSelectStock={setSelectedStock} />
            )}
          </div>
        ) : viewMode === "calendar" ? (
          <EconomicCalendarView />
        ) : viewMode === "sectors" ? (
          <SectorAnalytics onSelectStock={(ticker) => {
            axios.get(`${API}/stocks/lookup/${ticker}`).then(res => {
              if (res.data) setSelectedStock(res.data);
            }).catch(() => {});
          }} />
        ) : (
          <>
            <FilterRow
              statusFilter={statusFilter} sectorFilter={sectorFilter} sectors={sectors}
              onStatusChange={setStatusFilter} onSectorChange={setSectorFilter}
              resultCount={stocks.length}
            />

            <StockTable stocks={stocks} loading={loading} onSelectStock={setSelectedStock} watchlist={watchlist} />

            <DashboardBottomCards
              watchlist={watchlist}
              onSelectTicker={(ticker) => {
                if (!ticker) return;
                const t = String(ticker).toUpperCase();
                const existing = stocks.find((s) => s.ticker === t);
                if (existing) {
                  setSelectedStock(existing);
                } else {
                  handleLookup(t);
                }
              }}
              onOpenWatchlist={() => setViewMode("watchlist")}
            />
          </>
        )}
        <NewsFeed onOpenArticle={setArticleUrl} />

        {/* Disclaimer */}
        <footer className="mt-10 py-6 border-t space-y-4" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="disclaimer-section">
          <div className="flex items-start gap-2.5">
            <Warning size={16} weight="bold" className="text-zinc-500 mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400 leading-relaxed">
              This tool provides informational screening based on financial data and commonly used Shariah compliance criteria. It does not constitute financial or religious advice. Users should consult qualified professionals for final decisions.
            </p>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            This site collects anonymous usage data via analytics tools (Google Analytics and Microsoft Clarity). No personal information is stored and no login is required.{" "}
            <button onClick={() => onNavigate("privacy")} className="underline hover:text-zinc-300 transition-colors" data-testid="privacy-link">
              Privacy Policy
            </button>
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-[10px] font-mono text-zinc-500 tracking-wide">
              Real-time data via Financial Modeling Prep & Yahoo Finance. AAOIFI thresholds: Debt/MCap &lt; 33%, Interest/Rev &lt; 5%, Receivables/Assets &lt; 49%.
            </p>
            <button
              onClick={() => onNavigate("admin")}
              className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
              data-testid="admin-link"
            >
              <ShieldCheck size={10} weight="bold" />
              Admin
            </button>
          </div>
        </footer>
      </div>

      <StockDetail stock={selectedStock} open={!!selectedStock} onClose={() => setSelectedStock(null)} onOpenArticle={setArticleUrl} />
      {articleUrl && <ArticleReader url={articleUrl} onClose={() => setArticleUrl(null)} />}
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} trigger={upgradeTrigger} />
    </div>
  );
}
